/**
 * Admin Suggestions Board — Kanban of every user-submitted idea.
 *
 * Columns: New → Under Review → Planned → Completed / Declined.
 * Uses the shared KanbanColumn + MoveToDropdown primitives so it matches
 * the rest of the app's boards (Support / Orders / Invoices / Accounts).
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Home, Lightbulb, Loader2, Trash2, Sparkles, Clock, Layers, CheckCircle2, XCircle, Search, Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import useHomePath from '@/hooks/useHomePath';
import { formatApiError } from '@/utils/apiError';
import { KanbanColumn, MoveToDropdown, KANBAN_PALETTE } from '@/components/kanban/KanbanShared';

// Column order matches the lifecycle
const COLUMNS = [
  { key: 'new',           label: 'New',           colour: KANBAN_PALETTE.slate,   icon: Sparkles },
  { key: 'under_review',  label: 'Under Review',  colour: KANBAN_PALETTE.blue,    icon: Clock },
  { key: 'planned',       label: 'Planned',       colour: KANBAN_PALETTE.violet,  icon: Layers },
  { key: 'completed',     label: 'Completed',     colour: KANBAN_PALETTE.green,   icon: CheckCircle2 },
  { key: 'declined',      label: 'Declined',      colour: KANBAN_PALETTE.red,     icon: XCircle },
];
const MOVE_OPTIONS = COLUMNS.map((c) => ({ key: c.key, label: c.label }));

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const initials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export default function StaffSuggestionsBoard() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const homePath = useHomePath();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCard, setOpenCard] = useState(null);
  const [query, setQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState(''); // '', 'staff', 'installer'

  const isAdmin = ['admin', 'manager'].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/staff/suggestions`, { credentials: 'include' });
      if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))) || 'Failed');
      setSuggestions(await r.json());
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!user) return;
    if (!isAdmin) { navigate(homePath); return; }
    load();
  }, [user, isAdmin, homePath, navigate, load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return suggestions.filter((s) => {
      if (sourceFilter && s.submitter_source !== sourceFilter) return false;
      if (!q) return true;
      return `${s.title || ''} ${s.details || ''} ${s.submitter_name || ''}`.toLowerCase().includes(q);
    });
  }, [suggestions, query, sourceFilter]);

  const byColumn = useMemo(() => {
    const g = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));
    filtered.forEach((s) => { if (g[s.status]) g[s.status].push(s); });
    return g;
  }, [filtered]);

  const moveTo = async (s, target) => {
    if (s.status === target) return;
    // Optimistic
    setSuggestions((prev) => prev.map((x) => (x.id === s.id ? { ...x, status: target } : x)));
    try {
      const r = await fetch(`${API_URL}/api/staff/suggestions/${s.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: target }),
      });
      if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))) || 'Move failed');
      const fresh = await r.json();
      setSuggestions((prev) => prev.map((x) => (x.id === fresh.id ? fresh : x)));
      toast.success(`Moved to ${COLUMNS.find((c) => c.key === target)?.label}`);
    } catch (e) { toast.error(e.message); load(); }
  };

  const removeCard = async (s) => {
    if (!window.confirm(`Delete "${s.title}"? This can't be undone.`)) return;
    try {
      const r = await fetch(`${API_URL}/api/staff/suggestions/${s.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!r.ok) throw new Error(formatApiError(await r.json().catch(() => ({}))) || 'Failed');
      setSuggestions((prev) => prev.filter((x) => x.id !== s.id));
      setOpenCard(null);
      toast.success('Deleted');
    } catch (e) { toast.error(e.message); }
  };

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #7c2d12, #b45309)' }}>
        <div className="max-w-[1600px] mx-auto px-4 py-4 flex items-center gap-3 flex-wrap">
          <button onClick={() => navigate(homePath)} data-testid="sugg-board-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Home">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl font-bold flex items-center gap-2 truncate">
              <Lightbulb className="w-5 h-5 shrink-0" /> App Improvement Suggestions
            </h1>
            <p className="hidden sm:block text-white/80 text-xs">Every idea submitted by staff and installers, ready to triage.</p>
          </div>
          <Button onClick={() => navigate('/suggest')} size="sm" className="bg-white text-amber-900 hover:bg-amber-50 shrink-0" data-testid="sugg-board-new">
            <Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Make a suggestion</span>
          </Button>
          <Button onClick={() => navigate(homePath)} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0">
            <Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Home</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 py-4">
        {/* Filters — search + source */}
        <div className="mb-3 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search title, detail or submitter…"
              className="pl-9 h-10 bg-white"
              data-testid="sugg-search"
            />
          </div>
          {['', 'staff', 'installer'].map((k) => (
            <button
              key={k || 'all'}
              type="button"
              onClick={() => setSourceFilter(k)}
              className={`text-xs px-2.5 py-1 rounded-full border transition ${
                sourceFilter === k ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'
              }`}
              data-testid={`sugg-filter-${k || 'all'}`}
            >
              {k === '' ? 'All' : k === 'staff' ? 'From staff' : 'From installers'}
            </button>
          ))}
          <span className="text-[11px] text-slate-500 ml-auto">
            {filtered.length} of {suggestions.length}
          </span>
        </div>

        {loading ? (
          <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-500" /></div>
        ) : (
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-4 min-w-max">
              {COLUMNS.map((col) => {
                const rows = byColumn[col.key] || [];
                return (
                  <KanbanColumn
                    key={col.key}
                    column={col}
                    count={rows.length}
                    testId={`sugg-col-${col.key}`}
                    emptyLabel="Nothing here yet"
                  >
                    {rows.map((s) => (
                      <div
                        key={s.id}
                        onClick={() => setOpenCard(s)}
                        className="group bg-white border rounded-lg p-3 shadow-sm hover:shadow-md cursor-pointer transition"
                        data-testid={`sugg-card-${s.id}`}
                      >
                        <div className="flex items-start gap-2">
                          <div className="font-semibold text-sm text-gray-900 flex-1 min-w-0 line-clamp-2">{s.title}</div>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-1 line-clamp-2 whitespace-pre-wrap">{s.details}</p>
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-700 bg-slate-100 rounded-full pr-2 pl-0.5 py-0.5"
                            title={s.submitter_name}
                          >
                            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-900 text-white text-[8px]">
                              {initials(s.submitter_name)}
                            </span>
                            {s.submitter_name}
                          </span>
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                            s.submitter_source === 'staff' ? 'bg-indigo-100 text-indigo-700' : 'bg-amber-100 text-amber-800'
                          }`}>
                            {s.submitter_source === 'staff' ? 'Staff' : 'Installer'}
                          </span>
                          <span className="text-[10px] text-slate-400 ml-auto">{fmt(s.created_at)}</span>
                        </div>
                        <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                          <MoveToDropdown
                            current={s.status}
                            options={MOVE_OPTIONS}
                            onChange={(target) => moveTo(s, target)}
                            testId={`sugg-move-${s.id}`}
                          />
                        </div>
                      </div>
                    ))}
                  </KanbanColumn>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {openCard && (
        <SuggestionDetailModal
          suggestion={openCard}
          onClose={() => setOpenCard(null)}
          onDelete={() => removeCard(openCard)}
        />
      )}
    </div>
  );
}

function SuggestionDetailModal({ suggestion, onClose, onDelete }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" data-testid="sugg-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg my-8">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-slate-900 truncate">{suggestion.title}</h3>
            <div className="text-[11px] text-slate-400 font-mono">{suggestion.reference}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <XCircle className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div>
            <div className="text-[11px] font-bold uppercase text-slate-500 mb-1">Suggestion</div>
            <p className="text-slate-800 whitespace-pre-wrap">{suggestion.details}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs pt-2 border-t">
            <div>
              <div className="text-[11px] font-bold uppercase text-slate-500">From</div>
              <div>{suggestion.submitter_name}</div>
              {suggestion.submitter_email && <div className="text-slate-500">{suggestion.submitter_email}</div>}
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase text-slate-500">Sent</div>
              <div>{fmt(suggestion.created_at)}</div>
            </div>
          </div>
          {suggestion.history?.length > 1 && (
            <div className="pt-3 border-t">
              <div className="text-[11px] font-bold uppercase text-slate-500 mb-1">History</div>
              <ul className="text-xs text-slate-600 space-y-1">
                {[...suggestion.history].reverse().map((h, i) => (
                  <li key={i}>
                    <span className="text-slate-400">{fmt(h.at)} · </span>
                    <span className="font-semibold">{h.by_name}</span>
                    {' '}
                    {h.event === 'created' ? 'submitted this suggestion' : `moved from ${h.from} → ${h.to}`}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t bg-slate-50 rounded-b-xl">
          <Button variant="ghost" onClick={onDelete} className="text-rose-600 hover:bg-rose-50" data-testid="sugg-delete">
            <Trash2 className="w-4 h-4 mr-1" /> Delete
          </Button>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}
